import React, { useState, useRef, useEffect } from "react";
import { FiChevronLeft, FiChevronRight, FiChevronDown, FiCheck } from "react-icons/fi";

const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 50, 100];

export default function Pagination({
  currentPage = 1,
  setCurrentPage,
  onPageChange,
  pageSize = 10,
  setPageSize,
  onPageSizeChange,
  pageSizeOptions,
  totalCount = 0,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const pageSizeRef = useRef(null);

  // Ensure safe numeric values to prevent NaN calculation errors
  const safeCurrentPage = Number(currentPage) || 1;
  const safePageSize = Number(pageSize) || 10;
  const safeTotalCount = Number(totalCount) || 0;

  // Fallback handlers to support both callback styles
  const handlePageSizeChange = (newSize) => {
    if (typeof onPageSizeChange === "function") {
      onPageSizeChange(newSize);
    } else if (typeof setPageSize === "function") {
      setPageSize(newSize);
    }

    handlePageChange(1);
    setIsOpen(false);
  };

  const handlePageChange = (newPage) => {
    if (typeof onPageChange === "function") {
      onPageChange(newPage);
    } else if (typeof setCurrentPage === "function") {
      setCurrentPage(newPage);
    }
  };

  // Extract numeric array if options are passed as objects like { label, value }
  const rawOptions = pageSizeOptions || DEFAULT_PAGE_SIZE_OPTIONS;
  const options = rawOptions.map((opt) => (typeof opt === "object" ? opt.value : opt));

  // Calculations with safe fallbacks
  const totalPages = Math.max(1, Math.ceil(safeTotalCount / safePageSize));
  const startIndex = safeTotalCount > 0 ? (safeCurrentPage - 1) * safePageSize + 1 : 0;
  const endIndex = Math.min(safeCurrentPage * safePageSize, safeTotalCount);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pageSizeRef.current && !pageSizeRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Truncate page numbers when page count is high
  const getPaginationRange = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (safeCurrentPage <= 3) return [1, 2, 3, 4, "...", totalPages];
    if (safeCurrentPage >= totalPages - 2)
      return [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, "...", safeCurrentPage - 1, safeCurrentPage, safeCurrentPage + 1, "...", totalPages];
  };

  return (
    <div className="px-4 py-3 border-t border-zinc-200/80 bg-zinc-50/50 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
      {/* Left: Page Size Selector & Record Stats */}
      <div className="flex flex-wrap items-center gap-2">
        <span>Show</span>
        <div className="relative" ref={pageSizeRef}>
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center justify-between gap-2 px-2.5 py-1 bg-white border border-zinc-200/80 rounded-lg text-xs font-semibold text-zinc-800 hover:bg-zinc-50 focus:outline-none transition-colors cursor-pointer"
          >
            <span>{safePageSize}</span>
            <FiChevronDown
              className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {isOpen && (
            <div className="absolute bottom-full mb-1 left-0 w-24 bg-white border border-zinc-200/80 rounded-lg shadow-lg py-1 z-30 max-h-48 overflow-y-auto">
              {options.map((size) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => handlePageSizeChange(size)}
                  className={`w-full flex items-center justify-between px-3 py-1.5 text-xs text-left hover:bg-zinc-50 transition-colors cursor-pointer ${
                    safePageSize === size ? "font-bold text-zinc-900 bg-zinc-50" : "text-zinc-600"
                  }`}
                >
                  <span>{size}</span>
                  {safePageSize === size && <FiCheck className="w-3 h-3 text-zinc-700" />}
                </button>
              ))}
            </div>
          )}
        </div>
        <span>entries</span>
        <span className="text-zinc-300 mx-1">|</span>
        <span>
          Showing <strong className="text-zinc-800">{startIndex}</strong> to{" "}
          <strong className="text-zinc-800">{endIndex}</strong> of{" "}
          <strong className="text-zinc-800">{safeTotalCount}</strong> entries
        </span>
      </div>

      {/* Right: Page Navigation */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Previous Page"
          onClick={() => handlePageChange(Math.max(safeCurrentPage - 1, 1))}
          disabled={safeCurrentPage === 1}
          className="p-1.5 rounded-md border border-zinc-200/80 bg-white text-zinc-600 hover:bg-zinc-100 disabled:opacity-40 transition-colors cursor-pointer disabled:cursor-not-allowed"
        >
          <FiChevronLeft className="w-3.5 h-3.5" />
        </button>

        {getPaginationRange().map((item, idx) =>
          item === "..." ? (
            <span key={`ellipsis-${idx}`} className="px-1.5 py-1 text-zinc-400 select-none">
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              onClick={() => handlePageChange(item)}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors cursor-pointer ${
                safeCurrentPage === item
                  ? "bg-zinc-900 text-white"
                  : "bg-white border border-zinc-200/80 text-zinc-600 hover:bg-zinc-100"
              }`}
            >
              {item}
            </button>
          )
        )}

        <button
          type="button"
          aria-label="Next Page"
          onClick={() => handlePageChange(Math.min(safeCurrentPage + 1, totalPages))}
          disabled={safeCurrentPage === totalPages}
          className="p-1.5 rounded-md border border-zinc-200/80 bg-white text-zinc-600 hover:bg-zinc-100 disabled:opacity-40 transition-colors cursor-pointer disabled:cursor-not-allowed"
        >
          <FiChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}